import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FiX, FiEye, FiEyeOff } from "react-icons/fi";
import { useAuth } from "../context/AuthContext";
import { authAPI } from "../api/endpoints";
import { toast } from "react-toastify";

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();

  // Login form
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  // Forgot password flow
  const [view, setView] = useState("login"); // "login" | "forgot" | "reset"
  const [forgotEmail, setForgotEmail] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back!");
      navigate("/");
    } catch (err) {
      toast.error(err.response?.data?.message || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await authAPI.forgotPassword(forgotEmail);
      toast.success("Confirmation code sent to your email!");
      setView("reset");
    } catch (err) {
      toast.error(err.response?.data?.message || "Email not found");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await authAPI.resetPassword(resetCode, forgotEmail, newPassword);
      // API returns token + user on successful reset — auto-login
      if (res.data?.token) {
        localStorage.setItem("auth_token", res.data.token);
        localStorage.setItem("user", JSON.stringify(res.data.user));
        toast.success("Password reset successful! Welcome back!");
        navigate("/");
        return;
      }
      toast.success("Password reset successful! Please sign in.");
      setView("login");
      setEmail(forgotEmail);
    } catch (err) {
      toast.error(err.response?.data?.message || "Reset failed. Check your code.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-5 pt-24 pb-10 bg-dark-900 relative overflow-x-hidden">
      {/* Glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[90vw] max-w-[600px] h-[400px] bg-primary/5 rounded-full blur-3xl" />

      <div className="w-full max-w-md bg-dark-700 border border-dark-400 rounded-2xl p-8 md:p-10 relative z-10 shadow-2xl">
        {/* Close (X) button */}
        <button
          onClick={() => navigate(-1)}
          className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-gray-400 hover:text-white transition-colors"
          aria-label="Close"
        >
          <FiX size={20} />
        </button>

        {/* ── LOGIN VIEW ── */}
        {view === "login" && (
          <>
            <h1 className="text-2xl font-bold text-center mb-2">Welcome Back</h1>
            <p className="text-center text-gray-500 text-sm mb-8">Sign in to continue streaming</p>

            <form onSubmit={handleLogin} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1.5">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  className="w-full px-4 py-3 bg-dark-600 border border-dark-400 rounded-lg text-white text-sm placeholder:text-gray-600 focus:border-primary focus:ring-1 focus:ring-primary/30 outline-none transition-all"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1.5">Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    required
                    className="w-full px-4 py-3 pr-11 bg-dark-600 border border-dark-400 rounded-lg text-white text-sm placeholder:text-gray-600 focus:border-primary focus:ring-1 focus:ring-primary/30 outline-none transition-all"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors"
                  >
                    {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                  </button>
                </div>
              </div>

              <div className="flex justify-between items-center text-sm">
                <label className="flex items-center gap-2 text-gray-400 cursor-pointer">
                  <input type="checkbox" className="rounded border-dark-400" /> Remember me
                </label>
                <button
                  type="button"
                  onClick={() => setView("forgot")}
                  className="text-primary hover:text-primary-hover font-medium"
                >
                  Forgot password?
                </button>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full btn-play-gradient disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? "Signing in..." : "Sign In"}
              </button>
            </form>

            <p className="text-center text-sm text-gray-500 mt-6">
              Don't have an account?{" "}
              <Link to="/register" className="text-primary font-semibold hover:text-primary-hover">
                Sign up free
              </Link>
            </p>
          </>
        )}

        {/* ── FORGOT PASSWORD VIEW ── */}
        {view === "forgot" && (
          <>
            <h1 className="text-2xl font-bold text-center mb-2">Forgot Password</h1>
            <p className="text-center text-gray-500 text-sm mb-8">
              Enter your registered email to receive a confirmation code
            </p>

            <form onSubmit={handleForgotPassword} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1.5">Email Address</label>
                <input
                  type="email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  placeholder="your-registered@email.com"
                  required
                  className="w-full px-4 py-3 bg-dark-600 border border-dark-400 rounded-lg text-white text-sm placeholder:text-gray-600 focus:border-primary focus:ring-1 focus:ring-primary/30 outline-none transition-all"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full btn-play-gradient disabled:opacity-50"
              >
                {loading ? "Sending..." : "Send Confirmation Code"}
              </button>
            </form>

            <button
              onClick={() => setView("login")}
              className="w-full text-center text-sm text-gray-500 mt-5 hover:text-white transition-colors"
            >
              &larr; Back to Sign In
            </button>
          </>
        )}

        {/* ── RESET PASSWORD VIEW ── */}
        {view === "reset" && (
          <>
            <h1 className="text-2xl font-bold text-center mb-2">Reset Password</h1>
            <p className="text-center text-gray-500 text-sm mb-8">
              Enter the code sent to <span className="text-white">{forgotEmail}</span>
            </p>

            <form onSubmit={handleResetPassword} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1.5">Confirmation Code</label>
                <input
                  type="text"
                  value={resetCode}
                  onChange={(e) => setResetCode(e.target.value)}
                  placeholder="Enter 6-digit code"
                  required
                  maxLength={6}
                  className="w-full px-4 py-3 bg-dark-600 border border-dark-400 rounded-lg text-white text-sm text-center tracking-[0.5em] font-mono text-lg placeholder:tracking-normal placeholder:text-sm placeholder:text-gray-600 focus:border-primary focus:ring-1 focus:ring-primary/30 outline-none transition-all"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1.5">New Password</label>
                <div className="relative">
                  <input
                    type={showNewPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                    required
                    minLength={8}
                    className="w-full px-4 py-3 pr-11 bg-dark-600 border border-dark-400 rounded-lg text-white text-sm placeholder:text-gray-600 focus:border-primary focus:ring-1 focus:ring-primary/30 outline-none transition-all"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors"
                  >
                    {showNewPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full btn-play-gradient disabled:opacity-50"
              >
                {loading ? "Resetting..." : "Reset Password"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
